import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FileText, Heart, Shield, MessageCircle,
  Zap, ArrowRight, Sparkles, Lock,
  Globe, Star, UsersRound, Smartphone, CopyCheck
} from 'lucide-react';
import { SiAppstore } from 'react-icons/si';
import Logo from './ui/Logo';
import LandingAppDownloadBar from './common/LandingAppDownloadBar';
import LandingPainQuotes from './common/LandingPainQuotes';
import PricingPlans from './common/PricingPlans';
import { useLanguage } from '../context/LanguageContext';
import { getTranslation } from '../translations';
import { APP_STORE_URL, PLAY_STORE_URL } from '../utils/appDownloadPromoStorage';
import { recordWebsiteVisit } from '../utils/recordWebsiteVisit';

function LandingGooglePlayMark({ size = 28 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden>
      <path fill="#00D9FF" d="M11.128 12.003 1.09 22.011a2.73 2.73 0 0 0 3.999 1.62l11.29-6.377-5.251-5.25Z" />
      <path fill="#00F076" d="m21.338 9.67-4.899-2.806-5.311 5.14 5.251 5.25 4.944-2.865a2.663 2.663 0 0 0 0-4.719h.015Z" />
      <path fill="#FFD23F" d="M1.09 1.995c-.06.225-.091.457-.09.69V21.32c0 .233.03.465.09.69l10.038-10.007L1.09 1.995Z" />
      <path fill="#FF1744" d="m11.128 12.003 5.311-5.139L5.126.383A2.79 2.79 0 0 0 3.723 0 2.73 2.73 0 0 0 1.09 1.988l10.038 10.015Z" />
    </svg>
  );
}

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.1, ease: [0.25, 0.46, 0.45, 0.94] }
  })
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.2 }
  }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }
  }
};

/** Smaller hero slogan: black line + purple line, staggered word reveal */
function AnimatedHeroHeadline({ part1, part2 }) {
  const line1 = String(part1 || '').trim();
  const line2 = String(part2 || '').trim();
  const words1 = line1.split(/\s+/).filter(Boolean);
  const words2 = line2.split(/\s+/).filter(Boolean);

  return (
    <h1 className="text-[1.65rem] xs:text-3xl sm:text-4xl md:text-[2.75rem] lg:text-5xl font-bold tracking-tight leading-[1.2] mb-6 px-1">
      <span className="block text-gray-900" aria-label={line1}>
        {words1.map((word, i) => (
          <span
            key={`h1-${i}-${word}`}
            className="hero-word inline-block me-[0.28em] last:me-0"
            style={{ animationDelay: `${0.12 + i * 0.06}s` }}
          >
            {word}
          </span>
        ))}
      </span>
      <span className="block mt-1.5 text-[#7B2FF7]" aria-label={line2}>
        {words2.map((word, i) => (
          <span
            key={`h2-${i}-${word}`}
            className="hero-word inline-block me-[0.28em] last:me-0"
            style={{ animationDelay: `${0.12 + (words1.length + i) * 0.06}s` }}
          >
            {word}
          </span>
        ))}
      </span>
    </h1>
  );
}

const LandingPage = () => {
  const { language, isRTL } = useLanguage();
  const t = (key, replacements) => getTranslation(key, language, replacements);
  const [landingBarBottomInset, setLandingBarBottomInset] = React.useState(0);

  useEffect(() => {
    recordWebsiteVisit();
  }, []);

  const features = [
    {
      icon: <FileText className="w-6 h-6" />,
      title: t('landingFeatureDigitalTitle'),
      description: t('landingFeatureDigitalDesc'),
      color: "from-purple-500 to-purple-600",
      bgColor: "bg-purple-50",
      textColor: "text-purple-600"
    },
    {
      icon: <Heart className="w-6 h-6" />,
      title: t('landingFeatureTrackTitle'),
      description: t('landingFeatureTrackDesc'),
      color: "from-pink-500 to-pink-600",
      bgColor: "bg-pink-50",
      textColor: "text-pink-600"
    },
    {
      icon: <UsersRound className="w-6 h-6" />,
      title: t('landingFeatureChildrenTitle'),
      description: t('landingFeatureChildrenDesc'),
      color: "from-indigo-500 to-indigo-600",
      bgColor: "bg-indigo-50",
      textColor: "text-indigo-600"
    },
    {
      icon: <MessageCircle className="w-6 h-6" />,
      title: t('landingFeatureMessageTitle'),
      description: t('landingFeatureMessageDesc'),
      color: "from-emerald-500 to-emerald-600",
      bgColor: "bg-emerald-50",
      textColor: "text-emerald-600"
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: t('landingFeaturePrivacyTitle'),
      description: t('landingFeaturePrivacyDesc'),
      color: "from-amber-500 to-amber-600",
      bgColor: "bg-amber-50",
      textColor: "text-amber-600"
    },
    {
      icon: <Globe className="w-6 h-6" />,
      title: t('landingFeatureLangTitle'),
      description: t('landingFeatureLangDesc'),
      color: "from-cyan-500 to-cyan-600",
      bgColor: "bg-cyan-50",
      textColor: "text-cyan-600"
    },
    {
      icon: <CopyCheck className="w-6 h-6" />,
      title: t('landingFeatureDuplicateTitle'),
      description: t('landingFeatureDuplicateDesc'),
      color: "from-violet-500 to-violet-600",
      bgColor: "bg-violet-50",
      textColor: "text-violet-600"
    }
  ];

  const steps = [
    { step: "01", title: t('landingStep1Title'), description: t('landingStep1Desc') },
    { step: "02", title: t('landingStep2Title'), description: t('landingStep2Desc') },
    { step: "03", title: t('landingStep3Title'), description: t('landingStep3Desc') }
  ];

  const dir = isRTL ? 'rtl' : 'ltr';

  return (
    <>
    <div
      className="landing-page min-h-screen bg-white overflow-hidden"
      dir={dir}
      style={landingBarBottomInset ? { paddingBottom: landingBarBottomInset } : undefined}
    >
      {/* Hero Section */}
      <section className="relative pt-24 sm:pt-28 pb-24 sm:pb-32 px-4 xs:px-5 sm:px-6 lg:px-8">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full opacity-[0.07]" style={{ background: 'radial-gradient(circle, #7B2FF7, transparent 70%)' }} />
          <div className="absolute -bottom-40 -left-40 w-[600px] h-[600px] rounded-full opacity-[0.05]" style={{ background: 'radial-gradient(circle, #9F44FF, transparent 70%)' }} />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full opacity-[0.03]" style={{ background: 'radial-gradient(circle, #7B2FF7, transparent 60%)' }} />
        </div>

        <div className="max-w-6xl mx-auto relative">
          <motion.div
            className="text-center max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            <div className="flex justify-center mb-8">
              <Logo height={56} />
            </div>

            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium mb-8" style={{ backgroundColor: '#f5f0ff', borderColor: '#ede5ff', color: '#7B2FF7', border: '1px solid #ede5ff' }}>
              <Sparkles className="w-4 h-4" />
              {t('landingBadge')}
            </div>

            <AnimatedHeroHeadline part1={t('landingHero1')} part2={t('landingHero2')} />

            <p className="text-lg sm:text-xl text-gray-500 max-w-2xl mx-auto mb-10 leading-relaxed">
              {t('landingSubtitle')}
            </p>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-4 w-full max-w-md sm:max-w-none mx-auto px-1">
              <Link to="/register" className="group inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl text-white font-semibold text-lg shadow-xl shadow-purple-500/25 hover:shadow-2xl hover:shadow-purple-500/30 active:scale-[0.98] transition-all duration-300 sm:hover:-translate-y-0.5 w-full sm:w-auto" style={{ backgroundImage: 'linear-gradient(135deg, #7B2FF7, #9F44FF)', textDecoration: 'none' }}>
                {t('landingGetStarted')}
                <ArrowRight className={`w-5 h-5 transition-transform ${isRTL ? 'rotate-180 group-hover:-translate-x-1' : 'group-hover:translate-x-1'}`} />
              </Link>
              <Link to="/login" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl bg-gray-50 text-gray-700 font-semibold text-lg border border-gray-200 hover:bg-gray-100 active:bg-gray-200 transition-all duration-300 w-full sm:w-auto touch-manipulation" style={{ textDecoration: 'none' }}>
                {t('landingSignIn')}
              </Link>
            </div>

            <div className="flex flex-col items-center gap-3 mt-12 px-2 max-w-full">
              <p className="text-sm text-purple-700 font-medium text-center mb-0">{t('landingTrialNote')}</p>
              <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-sm text-gray-400">
              <div className="flex items-center gap-1.5"><Lock className="w-4 h-4" /><span>{t('landingSecure')}</span></div>
              <div className="w-1 h-1 rounded-full bg-gray-300" />
              <div className="flex items-center gap-1.5"><Zap className="w-4 h-4" /><span>{t('landingFree')}</span></div>
              <div className="w-1 h-1 rounded-full bg-gray-300" />
              <div className="flex items-center gap-1.5"><Star className="w-4 h-4" /><span>{t('landingTrusted')}</span></div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <LandingPainQuotes />

      {/* Features - Bento Grid */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gray-50/50">
        <div className="max-w-6xl mx-auto">
          <motion.div className="text-center mb-16" initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={staggerContainer}>
            <motion.p variants={fadeInUp} className="text-sm font-semibold tracking-wider uppercase mb-3" style={{ color: '#7B2FF7' }}>{t('landingFeaturesLabel')}</motion.p>
            <motion.h2 variants={fadeInUp} className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">
              {t('landingFeaturesTitle1')}{' '}
              <span className="bg-clip-text text-transparent" style={{ backgroundImage: 'linear-gradient(135deg, #7B2FF7, #9F44FF)' }}>{t('landingFeaturesTitle2')}</span>
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-gray-500 text-lg max-w-xl mx-auto">{t('landingFeaturesSubtitle')}</motion.p>
          </motion.div>

          <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={staggerContainer}>
            {features.map((feature, index) => (
              <motion.div key={index} variants={scaleIn} whileHover={{ y: -4, transition: { duration: 0.2 } }} className="group relative bg-white rounded-2xl border border-gray-100 p-7 hover:shadow-xl hover:shadow-gray-200/50 transition-all duration-300">
                <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl ${feature.bgColor} ${feature.textColor} mb-5 group-hover:scale-110 transition-transform duration-300`}>{feature.icon}</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-500 leading-relaxed text-sm">{feature.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <motion.div className="text-center mb-16" initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={staggerContainer}>
            <motion.p variants={fadeInUp} className="text-sm font-semibold tracking-wider uppercase mb-3" style={{ color: '#7B2FF7' }}>{t('landingHowLabel')}</motion.p>
            <motion.h2 variants={fadeInUp} className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">{t('landingHowTitle')}</motion.h2>
            <motion.p variants={fadeInUp} className="text-gray-500 text-lg max-w-xl mx-auto">{t('landingHowSubtitle')}</motion.p>
          </motion.div>

          <motion.div className="grid grid-cols-1 md:grid-cols-3 gap-8" initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={staggerContainer}>
            {steps.map((step, i) => (
              <motion.div key={step.step} variants={fadeInUp} custom={i} className="relative text-center">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl text-white text-xl font-bold mb-6 shadow-lg" style={{ background: 'linear-gradient(135deg, #7B2FF7, #9F44FF)', boxShadow: '0 10px 15px -3px rgba(123, 47, 247, 0.2)' }}>{step.step}</div>
                {i < steps.length - 1 && (<div className="hidden md:block absolute top-7 w-[calc(100%-80px)] h-[2px]" style={{ background: `linear-gradient(to ${isRTL ? 'left' : 'right'}, #d4bfff, #ede5ff)`, [isRTL ? 'right' : 'left']: 'calc(50% + 40px)' }} />)}
                <h3 className="text-lg font-semibold text-gray-900 mb-3">{step.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{step.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white via-violet-50/30 to-white">
        <div className="max-w-6xl mx-auto">
          <PricingPlans />
        </div>
      </section>

      {/* Mobile app */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-violet-50/40 via-white to-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            className="text-center"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={staggerContainer}
          >
            <motion.div
              variants={fadeInUp}
              custom={0}
              className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-6 shadow-lg text-white"
              style={{ background: 'linear-gradient(135deg, #7B2FF7, #9F44FF)', boxShadow: '0 10px 15px -3px rgba(123, 47, 247, 0.25)' }}
              aria-hidden
            >
              <Smartphone className="w-7 h-7" strokeWidth={1.75} />
            </motion.div>
            <motion.p variants={fadeInUp} custom={0.5} className="text-sm font-semibold tracking-wider uppercase mb-3" style={{ color: '#7B2FF7' }}>
              {t('landingMobileAppLabel')}
            </motion.p>
            <motion.h2 variants={fadeInUp} custom={1} className="text-3xl sm:text-4xl font-bold tracking-tight mb-4 text-gray-900">
              {t('landingMobileAppTitle')}
            </motion.h2>
            <motion.p variants={fadeInUp} custom={1.5} className="text-gray-500 text-lg max-w-xl mx-auto mb-10">
              {t('landingMobileAppSubtitle')}
            </motion.p>
            <motion.div
              variants={fadeInUp}
              custom={2}
              className="flex flex-col sm:flex-row items-stretch justify-center gap-4 max-w-lg sm:max-w-none mx-auto"
            >
              <a
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center justify-center gap-3 px-6 py-4 rounded-2xl bg-gray-900 text-white font-semibold border border-gray-800 shadow-lg shadow-gray-900/15 hover:bg-gray-800 active:scale-[0.98] transition-all duration-300 min-h-[56px]"
                style={{ textDecoration: 'none' }}
                aria-label={`${t('appStoreBadgeKicker')} ${t('appStoreBadgeTitle')}`}
              >
                <SiAppstore className="w-7 h-7 shrink-0 opacity-90 group-hover:opacity-100" aria-hidden />
                <span className="text-left rtl:text-right">
                  <span className="block text-[10px] font-medium uppercase tracking-wide text-gray-400 leading-tight">{t('appStoreBadgeKicker')}</span>
                  <span className="block text-base leading-tight">{t('appStoreBadgeTitle')}</span>
                </span>
              </a>
              <a
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center justify-center gap-3 px-6 py-4 rounded-2xl bg-white text-gray-900 font-semibold border border-gray-200 shadow-md hover:border-gray-300 hover:shadow-lg active:scale-[0.98] transition-all duration-300 min-h-[56px]"
                style={{ textDecoration: 'none' }}
                aria-label={`${t('googlePlayBadgeKicker')} ${t('googlePlayBadgeTitle')}`}
              >
                <span className="shrink-0 flex items-center justify-center w-9 h-9" aria-hidden>
                  <LandingGooglePlayMark size={32} />
                </span>
                <span className="text-left rtl:text-right">
                  <span className="block text-[10px] font-semibold uppercase tracking-wide text-gray-500 leading-tight">{t('googlePlayBadgeKicker')}</span>
                  <span className="block text-base leading-tight">{t('googlePlayBadgeTitle')}</span>
                </span>
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <motion.div className="max-w-4xl mx-auto text-center" initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={staggerContainer}>
          <motion.div variants={scaleIn} className="relative rounded-3xl p-8 sm:p-16 overflow-hidden" style={{ background: 'linear-gradient(135deg, #3b1578 0%, #7B2FF7 50%, #9F44FF 100%)' }}>
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-white blur-3xl" />
              <div className="absolute bottom-0 left-0 w-48 h-48 rounded-full bg-white blur-3xl" />
            </div>
            <div className="relative">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">{t('landingCtaTitle')}</h2>
              <p className="text-purple-100 text-lg mb-8 max-w-xl mx-auto">{t('landingCtaSubtitle')}</p>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-4 px-2">
                <Link to="/register" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl bg-white font-semibold text-lg transition-all duration-300 shadow-xl active:scale-[0.98] w-full sm:w-auto touch-manipulation" style={{ color: '#7B2FF7', textDecoration: 'none' }}>
                  {t('landingCtaButton')}
                  <ArrowRight className={`w-5 h-5 ${isRTL ? 'rotate-180' : ''}`} />
                </Link>
                <Link to="/login" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl bg-white/10 text-white font-semibold text-lg border border-white/20 hover:bg-white/20 active:bg-white/25 transition-all duration-300 backdrop-blur-sm w-full sm:w-auto touch-manipulation" style={{ textDecoration: 'none' }}>
                  {t('landingSignIn')}
                </Link>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-100 py-12 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <Logo height={32} />
            </div>
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-gray-500">
              <Link to="/about" className="hover:text-gray-900 transition-colors" style={{ textDecoration: 'none' }}>{t('landingFooterAbout')}</Link>
              <Link to="/faq" className="hover:text-gray-900 transition-colors" style={{ textDecoration: 'none' }}>{t('landingFooterFaq')}</Link>
              <Link to="/terms" className="hover:text-gray-900 transition-colors" style={{ textDecoration: 'none' }}>{t('landingFooterTerms')}</Link>
              <Link to="/privacy" className="hover:text-gray-900 transition-colors" style={{ textDecoration: 'none' }}>{t('landingFooterPrivacy')}</Link>
              <Link to="/contact" className="hover:text-gray-900 transition-colors" style={{ textDecoration: 'none' }}>{t('landingFooterContact')}</Link>
            </div>
            <p className="text-sm text-gray-400">&copy; {new Date().getFullYear()} Resunize</p>
          </div>
          <p className="text-center text-sm text-purple-700 font-medium mt-6 mb-0">{t('brandSlogan')}</p>
        </div>
      </footer>
    </div>
    <LandingAppDownloadBar onBottomInsetChange={setLandingBarBottomInset} />
    </>
  );
};

export default LandingPage;
